import React, { useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DigitalCase,
  DigitalGroundTruth,
  DigitalGroundTruthTypes,
  DigitalQuestion,
  DigitalSection,
  GroundTruthMultiChoice,
  GroundTruthMultiChoiceWithText,
  GroundTruthRatio,
  GroundTruthSingleChoiceWithText,
  GroundTruthString,
  GTNumberRangePayload,
  GTSingleChoicePayload,
  PartialBy,
  QuestionCaseSetting,
  QuestionTypeV2,
} from '../../redux/types';
import GenericDialog from '../GenericDialog';
import GTNumberRangeScoreEditor from '../groundtruth/GTNumberRangeScoreEditor';
import GTSingleChoiceEditor from '../groundtruth/GTSingleChoiceEditor';
import GTMultiChoiceEditor from '../groundtruth/GTMultiChoiceEditor';
import GTRatioEditor from '../groundtruth/GTRatioEditor';
import GTStringScoreEditor from '../groundtruth/GTStringScoreEditor';
import GTSingleChoiceWithTextEditor from '../groundtruth/GTSingleChoiceWithTextEditor';
import GTMultiChoiceWithTextEditor from '../groundtruth/GTMultiChoiceWithTextEditor';
import GTScores from '../groundtruth/GTScores';
import useUpdateGtAndMaxScores from '../../hooks/groundTruth/useUpdateGtAndMaxScores';

type Props = {
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  digitalSection?: DigitalSection;
  digitalSurveyId: number;
  DGTDictionaryByQuestionAndCase: Record<number, Record<number, DigitalGroundTruth> | undefined>;
  settingsByQuestionAndCase?: {get: (qId: number, cId: number) => QuestionCaseSetting | undefined};
  submissionExist?: boolean;
  groundTruthGroupId: number|null;
  addOrUpdateGroundTruth: (dgt: PartialBy<DigitalGroundTruth, 'id'>) => void;
  deleteGroundTruth: (dgtId: number) => void;
  showErrorNotification?: (val: string) => void;
};

const DigitalGroundTruthEditor: React.FC<Props> = ({
  digitalSurveyId,
  digitalSection,
  digitalQuestions,
  digitalCases,
  DGTDictionaryByQuestionAndCase,
  settingsByQuestionAndCase,
  submissionExist,
  groundTruthGroupId,
  addOrUpdateGroundTruth,
  deleteGroundTruth,
  showErrorNotification,
}: Props) => {
  const { updateMaxScoresOnAddGT, updateMaxScoresOnDeleteGT } = useUpdateGtAndMaxScores();
  const [openScoreEditor, setOpenScoreEditor] = useState<boolean>(false);
  const [gtBuffer, setGtBuffer] = useState<PartialBy<DigitalGroundTruth, 'id' | 'payload'>>();
  // Question buffer holds the question whose GT will be added/updated
  const [questionBuffer, setQuestionBuffer] = useState<DigitalQuestion>();
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    rClickedQid: number;
    rClickedCid: number;
    rClickedQuestion: DigitalQuestion;
  } | null>(null);
  const [copyBuffer, setCopyBuffer] = useState<DigitalGroundTruth>();

  const onAddEditGT = (dgtlQuestionId: number, dgtlCaseId: number, question: DigitalQuestion) => {
    if (settingsByQuestionAndCase
        && settingsByQuestionAndCase.get(dgtlQuestionId, dgtlCaseId)?.hide) {
      showErrorNotification?.('Cannot set Groundtruth because question is marked as hidden');
      return;
    }
    if ([
      QuestionTypeV2.NumberRange,
      QuestionTypeV2.SingleChoice,
      QuestionTypeV2.MultiChoice,
      QuestionTypeV2.Ratio,
      QuestionTypeV2.String,
      QuestionTypeV2.SingleChoiceWithText,
      QuestionTypeV2.MultiChoiceWithText,
    ].includes(question.type)) {
      setOpenScoreEditor(true);
    }
    const existingGTRecord = DGTDictionaryByQuestionAndCase[dgtlQuestionId]?.[dgtlCaseId];
    if (existingGTRecord) {
      /** Edit Mode */
      setGtBuffer(existingGTRecord);
      setQuestionBuffer(question);
    } else {
      if (!digitalSection) {
        return;
      }
      /** Add mode */
      const gtDataBuffer: Omit<DigitalGroundTruth, 'id' | 'payload'> = {
        digitalSurveyId,
        digitalSectionId: digitalSection.id,
        digitalCaseId: dgtlCaseId,
        digitalQuestionId: dgtlQuestionId,
        type: question.type as DigitalGroundTruthTypes,
        digitalGroundTruthGroupId: groundTruthGroupId || null,
      };
      setGtBuffer(gtDataBuffer);
      setQuestionBuffer(question);
    }
  };

  const onDeleteGtScore = (dgtId: number) => {
    if (submissionExist) {
      showErrorNotification?.('Cannot delete Ground-Truth score, submission already exists');
      return;
    }

    if (dgtId && deleteGroundTruth) {
      deleteGroundTruth(dgtId);
      updateMaxScoresOnDeleteGT(dgtId);
    }
    setOpenScoreEditor(false);
    setGtBuffer(undefined);
    setQuestionBuffer(undefined);
  };

  const onSaveGroundTruthScore = (gtData: PartialBy<DigitalGroundTruth, 'id'>) => {
    if (submissionExist) {
      showErrorNotification?.('Cannot edit Ground-Truth score, submission already exists');
      return;
    }
    addOrUpdateGroundTruth(gtData);
    updateMaxScoresOnAddGT(gtData);
    setOpenScoreEditor(false);
    setGtBuffer(undefined);
    setQuestionBuffer(undefined);
  };

  const onCancelEditGtScore = () => {
    setOpenScoreEditor(false);
    setGtBuffer(undefined);
    setQuestionBuffer(undefined);
  };

  const handleContextMenu = (
    event: React.MouseEvent, questionId: number, caseId: number, question: DigitalQuestion,
  ) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
          rClickedQid: questionId,
          rClickedCid: caseId,
          rClickedQuestion: question,
        } : null,
    );
  };

  /**
   * DigitalGroundTruth Copy event handler
   */
  const handleGtCopy = () => {
    if (contextMenu === null) {
      setCopyBuffer(undefined);
    } else {
      const selectedGt = DGTDictionaryByQuestionAndCase[
        contextMenu.rClickedQid]?.[contextMenu.rClickedCid];
      if (selectedGt) {
        setCopyBuffer(selectedGt);
      }
    }
    setContextMenu(null);
  };

  /**
   * DigitalGroundtruth paste event handler
   */
  const handleGTPaste = () => {
    if (!copyBuffer || !contextMenu || !digitalSection) {
      setContextMenu(null);
      return;
    }
    const { rClickedQid, rClickedCid, rClickedQuestion } = contextMenu;
    if (copyBuffer.digitalQuestionId !== rClickedQuestion.id) {
      setContextMenu(null);
      showErrorNotification?.('Groundtruths can only be copied between the same questions');
      return;
    }
    const currentGT = DGTDictionaryByQuestionAndCase?.[rClickedQid]?.[rClickedCid];
    let gtObj: PartialBy<DigitalGroundTruth, 'id'>;
    if (!currentGT) {
      // groundTruth object as add
      gtObj = {
        digitalSurveyId,
        digitalSectionId: digitalSection.id,
        digitalCaseId: rClickedCid,
        digitalQuestionId: rClickedQid,
        type: rClickedQuestion.type as DigitalGroundTruthTypes,
        digitalGroundTruthGroupId: groundTruthGroupId || null,
        payload: copyBuffer.payload,
      };
    } else {
      // groundTruth object as update
      gtObj = {
        ...currentGT,
        payload: copyBuffer.payload,
      };
    }
    setGtBuffer(gtObj);
    setQuestionBuffer(rClickedQuestion);
    setOpenScoreEditor(true);
    setContextMenu(null);
  };

  const getCaseById = (id?: number) => digitalCases.find((dc) => dc.id === id);

  if (!digitalQuestions.length || !digitalCases || !digitalSection) {
    return null;
  }
  return (
    <>
      <TableContainer>
        <Table sx={{ position: 'relative', overflow: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  backgroundColor: 'white',
                  left: 0,
                  minWidth: 300,
                }}
              >
                Questions
              </TableCell>
              {
                digitalCases.map((dc) => (
                  <TableCell key={dc.id} align="center">{dc.name}</TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              digitalQuestions
                .map((dq) => (
                  <TableRow key={dq.id}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        backgroundColor: 'white',
                        left: 0,
                      }}
                    >
                      {dq.text}
                    </TableCell>
                    {
                      digitalCases.map((dc) => (
                        <TableCell
                          key={dc.id}
                          align="center"
                          sx={{
                            lineBreak: 'strict',
                            minWidth: '155px',
                            '&:hover': {
                              backgroundColor: 'ButtonFace',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease',
                            },
                          }}
                          onClick={() => { onAddEditGT(dq.id, dc.id, dq); }}
                          onContextMenu={
                            (e: React.MouseEvent) => handleContextMenu(e, dq.id, dc.id, dq)
                          }
                        >
                          <GTScores
                            question={dq}
                            groundTruth={DGTDictionaryByQuestionAndCase[dq.id]?.[dc.id]}
                          />
                        </TableCell>
                      ))
                    }
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleGtCopy}>Copy</MenuItem>
        <MenuItem disabled={!copyBuffer} onClick={handleGTPaste}>Paste</MenuItem>
      </Menu>
      <GenericDialog
        maxWidth="sm"
        isOpen={openScoreEditor}
        onRequestClose={() => { setOpenScoreEditor(false); }}
      >
        <Box sx={{ m: 1 }}>
          <Typography variant="h5" sx={{ m: 1 }}>
            {`Add or Edit Score for ${getCaseById(gtBuffer?.digitalCaseId)?.name}`}
          </Typography>
          {
            gtBuffer?.type === QuestionTypeV2.NumberRange
            && questionBuffer?.type === QuestionTypeV2.NumberRange && (
              <GTNumberRangeScoreEditor
                digitalSurveyId={gtBuffer.digitalSurveyId}
                digitalSectionId={gtBuffer.digitalSectionId}
                digitalQuestionId={gtBuffer.digitalQuestionId}
                digitalCaseId={gtBuffer.digitalCaseId}
                digitalGroundTruthId={gtBuffer.id}
                digitalGTPayload={gtBuffer.payload as GTNumberRangePayload}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                onDelete={onDeleteGtScore}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.SingleChoice
            && questionBuffer?.type === QuestionTypeV2.SingleChoice && (
              <GTSingleChoiceEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                payload={gtBuffer.payload as GTSingleChoicePayload}
                onDelete={onDeleteGtScore}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.MultiChoice
            && questionBuffer?.type === QuestionTypeV2.MultiChoice && (
              <GTMultiChoiceEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                payload={gtBuffer.payload as GroundTruthMultiChoice['payload']}
                onDelete={onDeleteGtScore}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.Ratio
            && questionBuffer?.type === QuestionTypeV2.Ratio && (
              <GTRatioEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                payload={gtBuffer.payload as GroundTruthRatio['payload']}
                onDelete={onDeleteGtScore}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.String
            && questionBuffer?.type === QuestionTypeV2.String && (
              <GTStringScoreEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                onDelete={onDeleteGtScore}
                payload={gtBuffer.payload as GroundTruthString['payload']}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.SingleChoiceWithText
            && questionBuffer?.type === QuestionTypeV2.SingleChoiceWithText && (
              <GTSingleChoiceWithTextEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                payload={gtBuffer.payload as GroundTruthSingleChoiceWithText['payload']}
                onDelete={onDeleteGtScore}
              />
            )
          }
          {
            gtBuffer?.type === QuestionTypeV2.MultiChoiceWithText
            && questionBuffer?.type === QuestionTypeV2.MultiChoiceWithText && (
              <GTMultiChoiceWithTextEditor
                groundTruthId={gtBuffer.id}
                surveyId={gtBuffer.digitalSurveyId}
                sectionId={gtBuffer.digitalSectionId}
                questionId={gtBuffer.digitalQuestionId}
                caseId={gtBuffer.digitalCaseId}
                gtGroupId={gtBuffer.digitalGroundTruthGroupId}
                question={questionBuffer}
                onSave={onSaveGroundTruthScore}
                onCancel={onCancelEditGtScore}
                payload={gtBuffer.payload as GroundTruthMultiChoiceWithText['payload']}
                onDelete={onDeleteGtScore}
              />
            )
          }
        </Box>
      </GenericDialog>
    </>
  );
};

export default DigitalGroundTruthEditor;
